import React, { useState } from 'react';
import { profileService } from '../../../services/profileService';

export default function SecuritySettings({ profile, user, onUpdate }) {
  const [showEmailChange, setShowEmailChange] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [emailForm, setEmailForm] = useState({ newEmail: '', confirmEmail: '' });
  const [passwordForm, setPasswordForm] = useState({ 
    currentPassword: '', 
    newPassword: '', 
    confirmPassword: '' 
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleEmailChange = async (e) => {
    e?.preventDefault();
    
    if (emailForm?.newEmail !== emailForm?.confirmEmail) {
      setError('Email addresses do not match');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await profileService?.updateEmail(emailForm?.newEmail);
      setSuccess('Verification email sent. Please check your inbox to confirm the email change.');
      setShowEmailChange(false);
      setEmailForm({ newEmail: '', confirmEmail: '' });
      
      // Log activity
      await profileService?.logActivity(
        user?.id,
        'email_change_requested',
        'Email change verification sent',
        null,
        navigator.userAgent
      );
    } catch (err) {
      setError(err?.message || 'Failed to update email');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e?.preventDefault();

    if (passwordForm?.newPassword !== passwordForm?.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (passwordForm?.newPassword?.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await profileService?.updatePassword(passwordForm?.newPassword);
      setSuccess('Password updated successfully');
      setShowPasswordChange(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      
      // Log activity
      await profileService?.logActivity(
        user?.id,
        'password_changed',
        'Password updated',
        null,
        navigator.userAgent
      );
    } catch (err) {
      setError(err?.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: '', color: '' };
    
    let strength = 0;
    if (password?.length >= 8) strength++;
    if (password?.length >= 12) strength++;
    if (/[a-z]/?.test(password) && /[A-Z]/?.test(password)) strength++;
    if (/\d/?.test(password)) strength++;
    if (/[^a-zA-Z0-9]/?.test(password)) strength++;

    const labels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong', 'Very Strong'];
    const colors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-blue-500', 'bg-green-500', 'bg-green-600'];

    return {
      strength,
      label: labels?.[strength],
      color: colors?.[strength]
    };
  };

  const passwordStrength = getPasswordStrength(passwordForm?.newPassword);

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-200 pb-4">
        <h2 className="text-xl font-semibold text-gray-900">Security Settings</h2>
        <p className="text-sm text-gray-500 mt-1">Manage your account security and authentication methods</p>
      </div>
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-sm text-green-600">{success}</p>
        </div>
      )}
      {/* Current Email */}
      <div className="bg-gray-50 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Email Address</h3>
            <p className="text-sm text-gray-500 mt-1">Your current email: {profile?.email}</p>
          </div>
          <button
            onClick={() => setShowEmailChange(!showEmailChange)}
            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            {showEmailChange ? 'Cancel' : 'Change Email'}
          </button>
        </div>

        {showEmailChange && (
          <form onSubmit={handleEmailChange} className="mt-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">New Email</label>
              <input
                type="email"
                value={emailForm?.newEmail}
                onChange={(e) => setEmailForm({ ...emailForm, newEmail: e?.target?.value })}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="new@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Email</label>
              <input
                type="email"
                value={emailForm?.confirmEmail}
                onChange={(e) => setEmailForm({ ...emailForm, confirmEmail: e?.target?.value })}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="new@example.com"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Updating...' : 'Send Verification Email'}
            </button>
          </form>
        )}
      </div>
      {/* Password Change */}
      <div className="bg-gray-50 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Password</h3>
            <p className="text-sm text-gray-500 mt-1">Update your password regularly to keep your account secure</p>
          </div>
          <button
            onClick={() => setShowPasswordChange(!showPasswordChange)}
            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            {showPasswordChange ? 'Cancel' : 'Change Password'}
          </button>
        </div>

        {showPasswordChange && (
          <form onSubmit={handlePasswordChange} className="mt-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
              <input
                type="password"
                value={passwordForm?.newPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e?.target?.value })}
                required
                minLength={8}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter new password"
              />
              {passwordForm?.newPassword && (
                <div className="mt-2">
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all ${passwordStrength?.color}`}
                        style={{ width: `${(passwordStrength?.strength / 5) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-xs text-gray-600 min-w-fit">{passwordStrength?.label}</span>
                  </div>
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
              <input
                type="password"
                value={passwordForm?.confirmPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e?.target?.value })}
                required
                minLength={8}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Confirm new password"
              />
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-900 mb-2">Password Requirements:</h4>
              <ul className="text-xs text-blue-700 space-y-1">
                <li>• At least 8 characters long</li>
                <li>• Mix of uppercase and lowercase letters</li>
                <li>• Include numbers and special characters</li>
                <li>• Avoid common words or patterns</li>
              </ul>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Updating...' : 'Update Password'}
            </button>
          </form>
        )}
      </div>
      {/* Two-Factor Authentication */}
      <div className="bg-gray-50 rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h3 className="text-sm font-medium text-gray-900">Two-Factor Authentication</h3>
            <p className="text-sm text-gray-500 mt-1">
              {profile?.twoFactorEnabled
                ? 'Two-factor authentication is enabled for your account'
                : 'Add an extra layer of security to your account'
              }
            </p>
          </div>
          <div className="ml-4">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
              profile?.twoFactorEnabled
                ? 'bg-green-100 text-green-800' :'bg-gray-100 text-gray-800'
            }`}>
              {profile?.twoFactorEnabled ? '✓ Enabled' : 'Disabled'}
            </span>
          </div>
        </div>
        <div className="mt-4">
          <button
            disabled
            className="text-sm text-gray-400 cursor-not-allowed"
          >
            Coming Soon: Set up two-factor authentication
          </button>
        </div>
      </div>
      {/* Account Deletion */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <h3 className="text-sm font-medium text-red-900 mb-2">Delete Account</h3>
        <p className="text-sm text-red-700 mb-4">
          Permanently delete your account and all associated data. This action cannot be undone.
        </p>
        <button
          disabled
          className="text-sm text-red-400 cursor-not-allowed"
        >
          Contact support to delete account
        </button>
      </div>
    </div>
  );
}